import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Upload, Trash2, Loader2, Film, ShieldCheck, Plus, Save, X,
  Tags, Inbox, ClipboardList, Check, RotateCcw, ChevronDown, ChevronUp,
  Users, Crown, Ban, RefreshCw, Home, FileEdit,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { value: "standard", label: "Standard" },
  { value: "vip", label: "VIP" },
];

const FIELD_TYPES = [
  { value: "text", label: "Single line" },
  { value: "textarea", label: "Multi line" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "yesno", label: "Yes / No" },
];

const emptyPkg = {
  id: null, name: "", tagline: "", price: "", features: "",
  tier: "standard", sort_order: 99, stripe_link: "",
};

export default function FormSetupTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/custom-form/config`);
      setConfig(r.data);
    } catch {
      toast.error("Failed to load form config");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateField = (idx, patch) => {
    setConfig((c) => {
      const fields = [...c.fields];
      fields[idx] = { ...fields[idx], ...patch };
      return { ...c, fields };
    });
  };

  const addField = () => {
    setConfig((c) => ({
      ...c,
      fields: [
        ...c.fields,
        {
          id: "",
          label: "New field",
          placeholder: "",
          help_text: "",
          type: "text",
          required: false,
          sort_order: (c.fields.at(-1)?.sort_order || 0) + 1,
        },
      ],
    }));
  };

  const removeField = (idx) => {
    setConfig((c) => ({ ...c, fields: c.fields.filter((_, i) => i !== idx) }));
  };

  const move = (idx, dir) => {
    setConfig((c) => {
      const fields = [...c.fields];
      const j = idx + dir;
      if (j < 0 || j >= fields.length) return c;
      [fields[idx], fields[j]] = [fields[j], fields[idx]];
      fields.forEach((f, i) => (f.sort_order = i + 1));
      return { ...c, fields };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/custom-form/config`, config);
      toast.success("Form saved");
      await load();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <div className="text-white/40 text-sm">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="form-setup-tab">
      <div className="rv-card rounded-xl p-7 space-y-5">
        <h2 className="font-display font-bold uppercase text-xl">Page copy</h2>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-white/60">Page title</Label>
          <Input
            value={config.page_title}
            onChange={(e) => setConfig({ ...config, page_title: e.target.value })}
            className="bg-black/40 border-white/10 text-white"
            data-testid="form-page-title"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-white/60">Subtitle / intro paragraph</Label>
          <Textarea
            rows={3}
            value={config.page_subtitle}
            onChange={(e) => setConfig({ ...config, page_subtitle: e.target.value })}
            className="bg-black/40 border-white/10 text-white"
            data-testid="form-page-subtitle"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest text-white/60">Disclaimer (shown in popup before submit)</Label>
          <Textarea
            rows={4}
            value={config.disclaimer}
            onChange={(e) => setConfig({ ...config, disclaimer: e.target.value })}
            className="bg-black/40 border-white/10 text-white"
            data-testid="form-disclaimer"
          />
        </div>
      </div>

      <div className="rv-card rounded-xl p-7">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display font-bold uppercase text-xl">Form fields</h2>
          <button onClick={addField} className="btn-ghost" data-testid="add-field-btn">
            <Plus size={14} /> Add field
          </button>
        </div>

        <div className="space-y-4 mt-6">
          {config.fields.map((f, idx) => (
            <div key={f.id || `idx-${idx}`} className="border border-white/10 rounded-lg p-5 bg-black/30" data-testid={`field-row-${idx}`}>
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="text-xs uppercase tracking-widest text-white/45">Field #{idx + 1}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => move(idx, -1)} className="text-white/55 hover:text-white p-1" data-testid={`field-up-${idx}`}>
                    <ChevronUp size={16} />
                  </button>
                  <button onClick={() => move(idx, 1)} className="text-white/55 hover:text-white p-1" data-testid={`field-down-${idx}`}>
                    <ChevronDown size={16} />
                  </button>
                  <button
                    onClick={() => removeField(idx)}
                    className="text-white/55 hover:text-[var(--rv-red)] inline-flex items-center gap-1 text-xs uppercase tracking-widest px-2"
                    data-testid={`field-remove-${idx}`}
                  >
                    <Trash2 size={12} /> Remove
                  </button>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-white/60">Label</Label>
                  <Input
                    value={f.label}
                    onChange={(e) => updateField(idx, { label: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid={`field-label-${idx}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-white/60">Placeholder</Label>
                  <Input
                    value={f.placeholder || ""}
                    onChange={(e) => updateField(idx, { placeholder: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid={`field-placeholder-${idx}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-white/60">Type</Label>
                  <select
                    value={f.type}
                    onChange={(e) => updateField(idx, { type: e.target.value })}
                    className="w-full h-10 bg-black/40 border border-white/10 text-white rounded-md px-3 text-sm"
                    data-testid={`field-type-${idx}`}
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-white/60">Helper text (optional)</Label>
                  <Input
                    value={f.help_text || ""}
                    onChange={(e) => updateField(idx, { help_text: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid={`field-help-${idx}`}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer pt-1 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={!!f.required}
                    onChange={(e) => updateField(idx, { required: e.target.checked })}
                    className="accent-[var(--rv-red)]"
                    data-testid={`field-required-${idx}`}
                  />
                  <span>Required</span>
                </label>
              </div>
            </div>
          ))}
          {config.fields.length === 0 && (
            <div className="text-white/40 text-sm">No fields yet — add one to get started.</div>
          )}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn-primary w-full justify-center" data-testid="save-form-btn">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={16} /> Save form</>}
      </button>

      <p className="text-xs uppercase tracking-widest text-white/40 text-center">
        Public form lives at <span className="text-white/70">/custom-sens</span> — share that link with custom buyers.
      </p>
    </div>
  );
}

